import type { ComponentProps, ReactElement } from "react";

import { Builder, type CompositionComponent } from "./builder";

export class BaseMultipleBuilder<
  C extends readonly [CompositionComponent, ...CompositionComponent[]],
> extends Builder<
  C,
  readonly ReactElement<ComponentProps<C[number]>, C[number]>[]
> {
  public type = "multiple" as const;
}

class RequiredMultipleBuilder<
  C extends readonly [CompositionComponent, ...CompositionComponent[]],
> extends BaseMultipleBuilder<C> {
  constructor(components: C) {
    super(components);
    this._required = true;
  }
}

class DefaultedMultipleBuilder<
  C extends readonly [CompositionComponent, ...CompositionComponent[]],
> extends BaseMultipleBuilder<C> {
  constructor(
    components: C,
    defaultElements: ReactElement<ComponentProps<C[number]>, C[number]>[],
  ) {
    super(components);
    this._default = defaultElements;
  }
}

export class MultipleBuilder<
  C extends readonly [CompositionComponent, ...CompositionComponent[]],
> extends BaseMultipleBuilder<C> {
  required(): RequiredMultipleBuilder<C> {
    const required = new RequiredMultipleBuilder<C>(this.components);
    for (const alias of this._aliases) {
      required.alias(alias);
    }
    return required;
  }

  default(
    value: ReactElement<ComponentProps<C[number]>, C[number]>[],
  ): DefaultedMultipleBuilder<C> {
    const defaulted = new DefaultedMultipleBuilder<C>(this.components, value);
    for (const alias of this._aliases) {
      defaulted.alias(alias);
    }
    return defaulted;
  }
}
